import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import ArrowCircleRightTwoToneIcon from "@mui/icons-material/ArrowCircleRightTwoTone";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import toastr from "toastr";
import { makePUTRequestOnUserService } from "../../Api/api";
import {
  StyledSelectInput,
  StyledTextInput,
} from "../StyledComponents/StyledInputs";
import { StyledChip } from "./NewRFQOrderDetails";
import { OrderDetailsInterface } from "./interfaces";

const InvoiceInfo = ({
  orderDetails,
  type,
}: {
  orderDetails: OrderDetailsInterface;
  type: string;
}) => {
  const [expandInvoiceInfo, setExpandInvoiceInfo] = useState(false);
  const [payoutGiven, setPayoutGiven] = useState<boolean>(false);
  const [utrNumber, setUtrNumber] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const serverPayoutGiven = (
      orderDetails as {
        payout_given?: boolean | null;
      }
    ).payout_given;
    setPayoutGiven(Boolean(serverPayoutGiven));
    setUtrNumber(orderDetails?.utr_number ?? "");
  }, [orderDetails]);

  const feeValue =
    Number(orderDetails?.minimum_order_value ?? 0) >= 100000 ? "1000" : "0";

  const handlePayoutGivenSelect = (event: SelectChangeEvent<string>) => {
    setPayoutGiven(event.target.value === "Yes");
  };

  const handleUpdatePayout = () => {
    if (!orderDetails?.uuid) {
      toastr.error("Missing order identifier");
      return;
    }
    setIsUpdating(true);
    const trimmedUtr = utrNumber.trim();
    const payload: any = {
      uuid: orderDetails.uuid,
      payout_given: payoutGiven,
      utr_number: trimmedUtr || null,
    };
    if (type === "partner") {
      payload.type = "partner";
    } else if (type === "client") {
      payload.type = "client";
    }
    makePUTRequestOnUserService(
      "/order_management_v2/update_payout_given/update/", payload
    )
      .then((response) => {
        if (response?.status === 200) {
          toastr.success("Invoice payout details updated");
        }
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          "Failed to update payout details";
        toastr.error(errorMessage);
      })
      .finally(() => setIsUpdating(false));
  };

  const invoiceFields = [
    { label: "Source", value: orderDetails?.source?.name ?? "" },
    { label: "Fee", value: feeValue },
  ];

  return (
    <Box sx={{ bgcolor: "#FFFFFF", p: 2, borderRadius: 5, mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Invoice Information</Typography>
        <StyledChip
          label={expandInvoiceInfo ? "Collapse" : "Expand"}
          onClick={() => {
            setExpandInvoiceInfo(!expandInvoiceInfo);
          }}
          expandOrCollapse={expandInvoiceInfo}
        />
      </Box>
      <Box
        component="hr"
        sx={{ borderColor: "#7659EE", opacity: 0.5, mb: 4, mt: 2 }}
      />
      <Collapse in={expandInvoiceInfo}>
        <Grid container spacing={2}>
          {invoiceFields.map((item) => (
            <Grid key={item.label} item xs={12} sm={4} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel
                  shrink
                  htmlFor={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </InputLabel>
                <StyledTextInput
                  id={`${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  value={item.value}
                  disabled
                />
              </FormControl>
            </Grid>
          ))}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth variant="standard">
              <InputLabel shrink htmlFor="utr-number">
                UTR Number
              </InputLabel>
              <StyledTextInput
                id="utr-number"
                value={utrNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setUtrNumber(event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                width: 200,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <FormControl fullWidth variant="standard" size="small">
                <InputLabel sx={{ fontWeight: 800 }} id="payout-given-label">
                  Payout Given
                </InputLabel>
                <Select
                  labelId="payout-given-label"
                  id="payout-given-select"
                  value={payoutGiven ? "Yes" : "No"}
                  label="Payout Given"
                  onChange={handlePayoutGivenSelect}
                  input={<StyledSelectInput />}
                >
                  <MenuItem value={"No"}>No</MenuItem>
                  <MenuItem value={"Yes"}>Yes</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                sx={{ mb: 0.5, ml: 1, color: "#7659EE", p: 0 }}
                onClick={handleUpdatePayout}
                disabled={isUpdating}
              >
                <ArrowCircleRightTwoToneIcon fontSize="large" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default InvoiceInfo;
